import React, { useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import classNames from "classnames";
import dayjs from "dayjs";

import Auth from "shared/@auth";

import { Dashboard } from "../DashboardPage/useDashboard";
import { useAgenda } from "ManagementTeam/AgendaPage/useAgenda";

import { Header } from "ManagementTeam/Shared/Header/Header";
import { AgendaBody } from "ManagementTeam/AgendaPage/Agenda/AgendaBody/AgendaBody";
import ActionItemsPdf from "./ActionItemsPdf";
import ShareModal from "ManagementTeam/ShareModal";
import YearFilterDropdown from "ManagementTeam/Shared/YearFilterDropdown";
import MonthLink from "ManagementTeam/Shared/MonthLink";

import { ReactComponent as ShareButton } from "../assets/Edit_Category_Share.svg";
import ActionsIcon from "../assets/Menu_Icon_Action_Items.svg";

import styles from "./ActionItemsPage.module.css";

export function ActionItemsPage({ meetingType }) {
  const { hasPermission } = Auth.useContainer();

  const readOnly = !hasPermission("management-team.admins");

  const { account } = Auth.useContainer();
  const {
    data: dashboardData,
    refetchDashboard,
    isFetching: isDashboardFetching,
  } = Dashboard.useContainer();
  const { actionItemsGet, actionItemSharePut } = useAgenda();
  const [showShareModal, setShowShareModal] = useState(false);
  const [isLoadingShare, setIsLoadingShare] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorShare, setErrorShare] = useState({ error: "" });

  const [users, setUsers] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [filteredActionItems, setFilteredActionItems] = useState([]);
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedMeetingDate, setSelectedMeetingDate] = useState("");
  const [pdf, setPdf] = useState([]);

  useEffect(() => {
    refetch();
  }, []);

  // Ensure dashboard data is fetched on initial load
  useEffect(() => {
    if (refetchDashboard) {
      refetchDashboard();
    }
  }, []);

  useEffect(() => {
    setUsers(
      (dashboardData && dashboardData.users && [...dashboardData.users]) || []
    );
  }, [dashboardData, dashboardData.users]);

  useEffect(() => {
    let arr = [];
    if (selectedYear && actionItems.length > 0) {
      arr = [
        ...new Set(
          actionItems
            .filter((a) => a.meetingYear == selectedYear)
            .sort((b, c) => new Date(c.meetingDate) - new Date(b.meetingDate))
            .flatMap((a) => a.meetingMonth)
        ),
      ];
    }

    setMonths(arr);

    if (
      selectedMonth &&
      arr &&
      arr.length > 0 &&
      arr.indexOf(selectedMonth) < 0
    ) {
      setSelectedMonth(null);
    }
  }, [selectedYear, actionItems]);

  useEffect(() => {
    let arr = [];
    let meetingDate = "";
    if (selectedMonth && selectedYear && actionItems.length > 0) {
      arr = actionItems.filter(
        (a) => a.meetingYear == selectedYear && a.meetingMonth == selectedMonth
      );
      meetingDate = (arr && arr.length > 0 && arr[0].meetingDate) || "";
    }

    setSelectedMeetingDate(meetingDate);
    setFilteredActionItems(arr);
  }, [selectedMonth, selectedYear, actionItems]);

  useMemo(() => {
    setPdf([]);

    const actions = filteredActionItems
      .filter((a) => a && a.id)
      .map((a) => {
        return {
          assignee: a.owner.name,
          assignedType:
            a.owner.name.toUpperCase().includes("PAUL TSIKA") ||
            a.owner.name.includes("Attorneys")
              ? "Attorney"
              : a.owner.name.includes("WWG") ||
                a.owner.name.includes("WWG Guest")
              ? "WWG"
              : "Diamond",
          title: a.title,
          description: a.description,
          notes: a.notes,
          votingStatus:
            a.decision && a.decision.votingStatus
              ? a.decision.votingStatus.toLowerCase()
              : "",
        };
      });

    setPdf([...actions]);
  }, [account, actionItems, filteredActionItems]);

  const refetch = () => {
    actionItemsGet.mutateAsync().then((a) => {
      //make years and months
      let years = [...new Set(a.flatMap((b) => b.meetingYear))].sort(
        (b, c) => c - b
      );
      setYears(years);

      if (
        years.length > 0 &&
        (selectedYear == null || years.indexOf(selectedYear) < 0)
      ) {
        setSelectedYear(years[0]);
      }

      setActionItems([...a]);
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchDashboard(),
        actionItemsGet.mutateAsync().then((a) => {
          let years = [...new Set(a.flatMap((b) => b.meetingYear))].sort(
            (b, c) => c - b
          );
          setYears(years);
          if (
            years.length > 0 &&
            (selectedYear == null || years.indexOf(selectedYear) < 0)
          ) {
            setSelectedYear(years[0]);
          }
          setActionItems([...a]);
        }),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const onCancelShare = () => {
    setErrorShare({ error: "" });
    setShowShareModal(false);
  };

  const onShared = async (emails, message) => {
    const body = {
      meetingYear: selectedYear,
      meetingMonth: selectedMonth,
      emails: emails,
      message: message,
      actionItems: filteredActionItems,
    };

    setIsLoadingShare(true);
    const result = await actionItemSharePut.mutateAsync(body);
    setIsLoadingShare(false);

    if (result && result.success) {
      setErrorShare({ error: "" });
      setShowShareModal(false);
      toast.success("Share Success!");
    } else {
      setErrorShare({ error: "Share failed! Please try again!" });
      toast.error("Share failed! Please try again!");
    }
  };

  return (
    <div className={styles["page-container"]}>
      <div className={styles.header}>
        <Header
          title={"Action Items"}
          icon={ActionsIcon}
          onRefresh={handleRefresh}
          isLoading={isRefreshing}
        />
      </div>
      <div className={styles.grid}>
        <div
          className={classNames(styles.transition, styles.menu, {
            [styles.hidden]: selectedMonth,
          })}
        >
          <div className={styles["year-container"]}>
            <YearFilterDropdown
              currentYear={years.length > 0 ? years[0] : null}
              yearOptions={years}
              onChangeYear={(a) => {
                setSelectedYear(a);
              }}
            />
          </div>
          {months.map((a) => (
            <MonthLink
              key={a}
              month={a}
              year={selectedYear}
              onClick={(month, year) => {
                setSelectedMonth(month);
              }}
            />
          ))}
        </div>
        <div
          className={classNames(styles.transition, styles.content, {
            [styles.hidden]: !selectedMonth,
          })}
        >
          <div className={styles.flex}>
            <button
              className={styles["nav-button"]}
              onClick={() => setSelectedMonth(null)}
            >
              <span className={styles["left-arrow"]} />
              <span>Action Items</span>
            </button>
            <div
              style={{ visibility: !readOnly ? "visible" : "hidden" }}
              className={styles["option-buttons"]}
            >
              {pdf && (
                <ActionItemsPdf
                  date={dayjs(selectedMeetingDate).format("MMMM DD, YYYY")}
                  actionItems={pdf}
                />
              )}
              <ShareButton onClick={() => setShowShareModal(true)} />
            </div>
          </div>
          <section>
            {showShareModal && (
              <ShareModal
                isOpen={showShareModal}
                isLoading={isLoadingShare}
                error={errorShare}
                onCollapse={() => onCancelShare()}
                onShare={({ emails, message }) => onShared(emails, message)}
              />
            )}
            <h1 className={styles["section-header"]}>
              {selectedMonth} {selectedYear} Action Items
            </h1>
            <div className={styles["action-items"]}>
              <AgendaBody
                agendas={filteredActionItems}
                itemOwners={[]}
                setIsModalOpen={() => {}}
                meetingType={meetingType}
                users={users || []}
                readOnly={readOnly}
                meetingDateOptions={[]}
                isActionItem={true}
                onAfterEditSuccess={() => refetch()}
                type="action items"
              />
            </div>
          </section>
        </div>
      </div>
      <Toaster position="bottom-center" />
    </div>
  );
}
